// Title-row "Categorization tools" menu, design §2: exactly two actions,
// each independently operable, plus a running-job label swap on the first
// item. The dialogs it opens (ReclassifyConfirmDialog, TellMeARuleBox) stay
// mounted (not conditionally rendered) so their in-session state -- a
// drafted-but-unsaved rule instruction, an in-flight confirm -- survives the
// menu closing per §6's "closing preserves the in-session draft" rule.
import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@moneyskyline/ui';
import { useTrackedJobs } from './jobRegistry';
import { ReclassifyConfirmDialog } from '../ReclassifyButton';
import { TellMeARuleBox } from '../../rules/TellMeARuleBox';

const RUNNING_STATUSES = new Set(['queued', 'running']);

export function CategorizationTools({
  className,
  onViewProgress,
}: {
  className?: string;
  /** Called instead of opening the confirmation dialog when a known job is already running -- the caller
   *  (TransactionsPage) decides how "view progress" is realized (e.g. focusing the page-level JobStatusRow). */
  onViewProgress?: () => void;
}) {
  const [reclassifyOpen, setReclassifyOpen] = useState(false);
  const [ruleOpen, setRuleOpen] = useState(false);
  const { activeJob } = useTrackedJobs();

  const jobIsRunning = Boolean(activeJob?.status && RUNNING_STATUSES.has(activeJob.status));

  return (
    <div className={className}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="h-11"
            aria-label="Categorization tools"
            data-testid="categorization-tools-button"
          >
            <span className="hidden sm:inline">Categorization tools</span>
            <span className="sm:hidden">Tools</span>
            <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            data-testid="tools-menu-reclassify"
            onSelect={() => (jobIsRunning ? onViewProgress?.() : setReclassifyOpen(true))}
          >
            <div className="flex flex-col">
              <span>{jobIsRunning ? 'View reclassification progress' : 'Reclassify transactions…'}</span>
              {!jobIsRunning && <span className="text-xs text-muted-foreground">Review categories automatically</span>}
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="tools-menu-rule" onSelect={() => setRuleOpen(true)}>
            <div className="flex flex-col">
              <span>Tell me a rule…</span>
              <span className="text-xs text-muted-foreground">Describe how to categorize activity</span>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ReclassifyConfirmDialog open={reclassifyOpen} onOpenChange={setReclassifyOpen} onViewProgress={onViewProgress} />
      <TellMeARuleBox open={ruleOpen} onOpenChange={setRuleOpen} />
    </div>
  );
}

export default CategorizationTools;
